/**
 * WeekDetailsDialog — companion to WeekCard.
 *
 * Shows the Mon–Fri course list for a single week so parents can see
 * exactly what their child will do before they pick Half day or Full day.
 *
 * Feb 2026 light-theme refresh:
 *   • White DialogContent on rsc-slate page; brand-blue headings; soft
 *     charcoal body copy.
 *   • Hero band uses a soft yellow→white gradient with a yellow Week
 *     badge so the page identity still pops.
 *   • Per-day rows use a colored left rail tied to the subject so the
 *     STEM rotation is visually obvious.
 *   • Course titles tagged with AM / PM chips for quick scan.
 *   • Sticky CTA footer ($245 Half day / $450 Full day) with descriptive
 *     sub-labels.
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import {
  Calendar, MapPin, Users, Clock, Sparkles,
  Beaker, Cpu, Wrench, Sigma, Trophy, Sun, Sunset, Info,
} from 'lucide-react';

// Same Mon → Fri rotation we use on the catalog card so colors stay
// consistent across the two surfaces. Tuned for light bg.
const SUBJECTS = [
  { label: 'Science',     icon: Beaker, text: 'text-rose-600',    border: 'border-rose-200',    chip: 'bg-rose-50',    rail: '#ef4444' },
  { label: 'Technology',  icon: Cpu,    text: 'text-blue-600',    border: 'border-blue-200',    chip: 'bg-blue-50',    rail: '#3b82f6' },
  { label: 'Engineering', icon: Wrench, text: 'text-emerald-600', border: 'border-emerald-200', chip: 'bg-emerald-50', rail: '#10b981' },
  { label: 'Math',        icon: Sigma,  text: 'text-violet-600',  border: 'border-violet-200',  chip: 'bg-violet-50',  rail: '#8b5cf6' },
  { label: 'Robotics',    icon: Trophy, text: 'text-amber-600',   border: 'border-amber-200',   chip: 'bg-amber-50',   rail: '#f59e0b' },
];
const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const WEEKEND_DAY_LABELS = ['Saturday', 'Sunday'];

function SessionChip({ type }) {
  if (!type || type === 'full_day') return null;
  const isAm = type === 'am';
  const Icon = isAm ? Sun : Sunset;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
        isAm
          ? 'bg-amber-50 text-amber-700 border border-amber-200'
          : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
      }`}
    >
      <Icon className="h-2.5 w-2.5" />
      {type}
    </span>
  );
}

function QuickFact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-rsc-slate border border-rsc-card-border">
      <Icon className="h-3.5 w-3.5 text-rsc-yellow flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-wider text-rsc-charcoal-muted font-bold leading-none">
          {label}
        </div>
        <div className="text-[11px] sm:text-xs text-rsc-brand-blue font-semibold leading-tight mt-0.5">
          {value}
        </div>
      </div>
    </div>
  );
}

export default function WeekDetailsDialog({
  open, onClose, camp, section, weekNum, dateRange, gradeLabel,
  days = [], onRegisterHalf, onRegisterFull, halfDayPrice = 245, fullDayPrice = 450,
  isK2 = false, isWeekend = false,
}) {
  if (!camp || !section) return null;

  // Group days by day_number → up to 2 courses (AM + PM) per day. Sort AM
  // first so the visual order matches the actual day.
  const byDay = {};
  for (const d of days) {
    const k = d.day_number;
    if (!byDay[k]) byDay[k] = [];
    byDay[k].push(d);
  }
  Object.values(byDay).forEach(arr => arr.sort((a, b) => {
    const order = { am: 0, full_day: 1, pm: 2 };
    return (order[a.session_type] ?? 1) - (order[b.session_type] ?? 1);
  }));

  const dayNums = isWeekend ? [1, 2] : [1, 2, 3, 4, 5];
  const dayLabels = isWeekend ? WEEKEND_DAY_LABELS : DAY_LABELS;

  // Synthesize a friendly title — strip the "Week N: " / "Weekend N: " prefix.
  const friendlyName = (camp.name || '')
    .replace(/^.*?(Week|Weekend) \d+:\s*/i, '')
    .replace(/—\s*Grades.*$/i, '')
    .trim();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] p-0 overflow-hidden border-rsc-card-border bg-white text-rsc-charcoal gap-0 font-poppins"
        data-testid="week-details-dialog"
      >
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(92vh - 86px)' }}>
          <DialogTitle className="sr-only">{isWeekend ? 'Weekend' : 'Week'} {weekNum} curriculum</DialogTitle>
          <DialogDescription className="sr-only">
            {isWeekend ? 'Sat–Sun' : 'Daily Mon–Fri'} course schedule for {dateRange} at {section.location_name}.
          </DialogDescription>

          {/* ─── HERO BAND ─── */}
          <div className="relative px-5 sm:px-7 pt-6 pb-5 bg-gradient-to-br from-amber-50 via-amber-50/40 to-white border-b border-rsc-card-border">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 inline-flex flex-col items-center justify-center rounded-xl bg-rsc-yellow text-rsc-brand-blue px-3 py-2 shadow-md shadow-amber-200/60">
                <span className="text-[10px] font-extrabold uppercase tracking-widest leading-none">{isWeekend ? 'Wknd' : 'Week'}</span>
                <span className="text-xl font-extrabold leading-none mt-0.5">{weekNum}</span>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg sm:text-2xl font-extrabold text-rsc-brand-blue leading-tight pr-10">
                  {friendlyName || 'Summer STEM Camp'}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs text-rsc-charcoal">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-3 w-3 text-rsc-charcoal-muted" /> {dateRange}
                  </span>
                  <span className="text-rsc-card-border">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3 w-3 text-rsc-charcoal-muted" /> {section.location_name}
                  </span>
                  {gradeLabel && (
                    <>
                      <span className="text-rsc-card-border">·</span>
                      <span className="inline-flex items-center gap-1.5">
                        <Users className="h-3 w-3 text-rsc-charcoal-muted" /> {gradeLabel}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              <QuickFact
                icon={Clock}
                label="Hours"
                value={isK2 ? '9 AM – 12 PM' : '9 AM – 4 PM'}
              />
              <QuickFact icon={Sparkles} label="Subjects" value={isWeekend ? '2 STEM' : '5 STEM'} />
              <QuickFact icon={Users} label="Class size" value="Small group" />
            </div>
          </div>

          {/* ─── CURRICULUM ─── */}
          <div className="px-5 sm:px-7 py-5">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-[11px] uppercase tracking-widest text-rsc-charcoal-muted font-bold">
                Daily curriculum
              </h3>
              <span className="text-[10px] text-rsc-charcoal-muted">{isWeekend ? 'Sat → Sun' : 'Mon → Fri rotation'}</span>
            </div>

            <ol className="space-y-2">
              {dayNums.map((dayNum, idx) => {
                const subject = SUBJECTS[dayNum - 1];
                const Icon = subject.icon;
                const dayCourses = byDay[dayNum] || [];
                return (
                  <li
                    key={dayNum}
                    className={`relative rounded-xl bg-rsc-slate border ${subject.border} border-l-4 px-3.5 py-3 transition-colors hover:bg-rsc-slate-soft`}
                    style={{ borderLeftColor: subject.rail }}
                    data-testid={`week-details-day-${dayNum}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className={`inline-flex items-center justify-center h-7 w-7 rounded-lg ${subject.chip}`}>
                        <Icon className={`h-3.5 w-3.5 ${subject.text}`} />
                      </span>
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="text-sm font-extrabold text-rsc-brand-blue">
                          {dayLabels[idx]}
                        </span>
                        <span className="text-rsc-card-border text-xs">·</span>
                        <span className={`text-xs font-bold uppercase tracking-wider ${subject.text}`}>
                          {subject.label}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 pl-[38px] space-y-1.5">
                      {dayCourses.length === 0 ? (
                        <span className="text-xs text-rsc-charcoal-muted italic">
                          Curriculum coming soon — same 5-subject STEM rotation.
                        </span>
                      ) : (
                        dayCourses.map((d, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <SessionChip type={d.session_type} />
                            <span className="inline-flex items-center gap-1 text-[13px] font-medium text-rsc-charcoal leading-snug min-w-0">
                              {d.course_title}
                              {(d.course_description || d.sample_activities?.length > 0 || d.learning_outcomes?.length > 0) && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className="flex-shrink-0 text-rsc-charcoal-muted hover:text-rsc-brand-blue transition-colors focus:outline-none"
                                    aria-label="View course details"
                                  >
                                    <Info className="h-3.5 w-3.5" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-80 max-h-72 overflow-y-auto p-3.5 bg-white border-rsc-card-border shadow-lg text-rsc-charcoal"
                                  align="start"
                                >
                                  <p className="text-[11px] font-extrabold uppercase tracking-wider text-rsc-charcoal-muted mb-1.5">
                                    {d.course_title}
                                  </p>
                                  {d.course_description && (
                                    <p className="text-xs text-rsc-charcoal leading-relaxed">
                                      {d.course_description}
                                    </p>
                                  )}
                                  {(() => {
                                    const activities = d.sample_activities?.length > 0
                                      ? d.sample_activities
                                      : d.learning_outcomes?.length > 0
                                        ? d.learning_outcomes
                                        : null;
                                    return activities ? (
                                      <div className="mt-3">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-rsc-charcoal-muted mb-1">
                                          Sample Activities
                                        </p>
                                        <ul className="space-y-1">
                                          {activities.map((item, j) => (
                                            <li key={j} className="flex items-start gap-1.5 text-xs text-rsc-charcoal">
                                              <span className="text-rsc-yellow font-bold mt-0.5 flex-shrink-0">•</span>
                                              <span>{item}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    ) : null;
                                  })()}
                                </PopoverContent>
                              </Popover>
                              )}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>

            <p className="mt-4 text-[11px] text-rsc-charcoal-muted leading-relaxed">
              {isK2
                ? 'K–2 camps run mornings only. Drop-off 9 AM · pick-up 12 PM. Snack provided.'
                : isWeekend
                  ? 'Weekend camps run Sat & Sun. Full-day campers bring a packed lunch · 12–1 PM lunch break. Half-day spots run AM (9 AM – 12 PM) or PM (1 PM – 4 PM).'
                  : 'Full-day campers bring a packed lunch · 12–1 PM lunch break. Half-day spots run AM (9 AM – 12 PM) or PM (1 PM – 4 PM).'}
            </p>
          </div>
        </div>

        {/* ─── STICKY CTA FOOTER ─── */}
        <div className="border-t border-rsc-card-border bg-white px-5 sm:px-7 py-3.5">
          <div className="grid grid-cols-2 gap-2.5">
            <Button
              variant="outline"
              onClick={onRegisterHalf}
              className="h-auto py-2.5 border-rsc-card-border bg-rsc-slate text-rsc-brand-blue hover:bg-rsc-slate-soft hover:text-rsc-brand-blue flex-col gap-0"
              data-testid="week-details-register-half"
            >
              <span className="text-[13px] font-extrabold leading-tight">
                Half day · from ${halfDayPrice}
              </span>
              <span className="text-[10px] text-rsc-charcoal-muted font-medium mt-0.5">
                {isK2 ? 'AM · 9 AM – 12 PM' : '3 hours · AM or PM'}
              </span>
            </Button>
            {!isK2 ? (
              <Button
                onClick={onRegisterFull}
                className="h-auto py-2.5 bg-rsc-yellow text-rsc-brand-blue hover:bg-rsc-yellow/90 font-extrabold flex-col gap-0"
                data-testid="week-details-register-full"
              >
                <span className="text-[13px] font-extrabold leading-tight">
                  Full day · ${fullDayPrice}
                </span>
                <span className="text-[10px] text-rsc-brand-blue/70 font-semibold mt-0.5">
                  9 AM – 4 PM · lunch break
                </span>
              </Button>
            ) : (
              <Button
                disabled
                className="h-auto py-2.5 opacity-50 cursor-not-allowed flex-col gap-0"
              >
                <span className="text-[13px] font-extrabold leading-tight">
                  Full day · K-2 AM only
                </span>
                <span className="text-[10px] text-rsc-charcoal-muted mt-0.5">
                  Mornings only at this age
                </span>
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
